import { convertLegacyRouteRef } from '@backstage/core-compat-api';
import catalogPlugin from '@backstage/plugin-catalog/alpha';
import { catalogPlugin as legacyCatalogPlugin } from '@backstage/plugin-catalog';
import { entityRouteRef } from '@backstage/plugin-catalog-react';
import { createCatalogEntityPageWithTabVisibility } from '../components/catalog/CatalogEntityPageWithTabVisibility';

/**
 * Catalog plugin overrides:
 *
 * page:catalog  → CustomCatalogPage (pagination, scrolling, Catalog Assistant)
 * page:catalog/entity → Native alpha entity page + EntityLayoutWrapper tab visibility
 *                        (per-user preferences, owner-hidden tabs, More tabs overflow)
 *
 * Both overrides keep the native routeRef bindings intact so that
 * useRouteRef(catalog.catalogIndex) and useRouteRef(catalog.catalogEntity) resolve correctly.
 *
 * Status: PARTIALLY BLOCKED (upstream route-ref typing boundary).
 * Last reviewed: 2026-06-29.
 * Upstream packages: `@backstage/plugin-catalog-react`, `@backstage/plugin-catalog`.
 */
const catalogFeature = catalogPlugin.withOverrides({
  extensions: [
    // Catalog index page — CustomCatalogPage with offset pagination + Catalog Assistant
    catalogPlugin.getExtension('page:catalog').override({
      factory(originalFactory) {
        return originalFactory({
          params: {
            path: '/catalog',
            noHeader: true,
            routeRef: legacyCatalogPlugin.routes.catalogIndex,
            loader: async () => {
              const { CustomCatalogPage } = await import(
                '../components/catalog/CustomCatalogPage'
              );
              return <CustomCatalogPage />;
            },
          },
        });
      },
    }),
    // Entity detail page — native blueprint wiring with tab visibility filtering.
    catalogPlugin.getExtension('page:catalog/entity').override({
      factory(originalFactory, { inputs }) {
        return originalFactory({
          params: {
            path: '/catalog/:namespace/:kind/:name',
            noHeader: true,
            // `entityRouteRef` is still legacy-typed upstream, so bridge it for frontend-plugin-api routeRef.
            routeRef: convertLegacyRouteRef(entityRouteRef),
            loader: async () =>
              createCatalogEntityPageWithTabVisibility(
                inputs as Parameters<
                  typeof createCatalogEntityPageWithTabVisibility
                >[0],
              ),
          } as any,
        });
      },
    }),
    // Disable the catalog plugin's auto-registered generic Overview blueprint.
    // We provide custom per-kind overviews in entityContentExtensionsModule
    // (component-overview, user-overview, group-overview, system-overview,
    // domain-overview, agent-overview). Without this, two Overview tabs appear.
    catalogPlugin.getExtension('entity-content:catalog/overview').override({
      disabled: true,
    }),
  ],
});

export default catalogFeature;



